import jsonServerProvider from 'ra-data-json-server';
import { DataProvider, GetListParams, GetManyParams, GetManyReferenceParams } from 'react-admin';
import Keycloak from "keycloak-js";
import { createHttpClient } from '../utils/http-client';
import { fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { ManualReviewParams } from '../models/request/ManualReviewParams';
import { UpdateVerifyStatusParams } from '../models/request/UpdateVerifyStatusParams';
import { RefPriceOrder } from '../models/response/RefPrice';
import { QueryLocationDriversParams } from '../models/request/QueryLocationDriversParams';
import type {
    AgentDailyReport,
    AgentDispatchRecord,
    AgentMatchOrderStats,
    AgentRelatedOrder,
} from '../models/AgentDispatchRecord';
import type { CreateTicketReplyInput, OssTokenCredentials, TicketDetail, TicketListItem, TicketReply } from '../models/Ticket';
import type { AppDataProvider } from '../types/dataProvider';

function buildUrl(base: string, paths: string[], params: Record<string, string | number | boolean | string[] | undefined>) {
    const path = paths.join('/');
    const query = stringify(params);
    return query ? `${base}/${path}?${query}` : `${base}/${path}`;
}

function extractUsersLoginToken(json: unknown): string | null {
    if (typeof json === 'string') {
        return json;
    }
    if (typeof json === 'object' && json !== null) {
        const record = json as Record<string, unknown>;
        if (typeof record.token === 'string') {
            return record.token;
        }
        if (typeof record.token === 'object' && record.token !== null) {
            const tokenRecord = record.token as Record<string, unknown>;
            if (typeof tokenRecord.token === 'string') {
                return tokenRecord.token;
            }
        }
        const data = record.data;
        if (typeof data === 'object' && data !== null) {
            const dataRecord = data as Record<string, unknown>;
            if (typeof dataRecord.token === 'string') {
                return dataRecord.token;
            }
        }
    }
    return null;
}

function pickString(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    return '';
}

function pickBoolean(value: unknown): boolean {
    return value === true;
}

function pickStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.map((item) => pickString(item)).filter(Boolean);
}

function toBase64(value: string): string {
    if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
        return window.btoa(value);
    }
    return btoa(value);
}

function encodeOssPolicy(policy: Record<string, unknown>): string {
    return toBase64(JSON.stringify(policy));
}

function createHmacSha1Signature(secret: string, message: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(message);
    return window.crypto.subtle
        .importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'])
        .then((key) => window.crypto.subtle.sign('HMAC', key, messageData))
        .then((signature) => {
            const bytes = new Uint8Array(signature);
            let binary = '';
            bytes.forEach((byte) => {
                binary += String.fromCharCode(byte);
            });
            return toBase64(binary);
        });
}

function getFileExtension(fileName: string): string {
    const trimmed = fileName.trim();
    const lastDotIndex = trimmed.lastIndexOf('.');
    if (lastDotIndex < 0) {
        return '';
    }
    return trimmed.slice(lastDotIndex).toLowerCase();
}

function sanitizeFileBaseName(fileName: string): string {
    const extension = getFileExtension(fileName);
    const baseName = extension ? fileName.slice(0, -extension.length) : fileName;
    const normalized = baseName.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');
    return normalized || 'image';
}

function buildTicketImageObjectKey(file: File): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const timestamp = now.getTime();
    const randomPart = Math.random().toString(36).slice(2, 10);
    const extension = getFileExtension(file.name) || '.jpg';
    const baseName = sanitizeFileBaseName(file.name);
    return `ticket-images/${year}/${month}/${day}/${baseName}-${timestamp}-${randomPart}${extension}`;
}

function normalizeOssTokenCredentials(value: unknown): OssTokenCredentials {
    const raw = unwrapData<unknown>(value);
    const record = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
    const tokenRecord = (typeof record.token === 'object' && record.token !== null
        ? record.token
        : typeof record.credentials === 'object' && record.credentials !== null
        ? record.credentials
        : typeof record.data === 'object' && record.data !== null
        ? ((record.data as Record<string, unknown>).token ?? record.data)
        : record) as Record<string, unknown>;

    const credentials: OssTokenCredentials = {
        accessKeyId: pickString(tokenRecord.accessKeyId ?? tokenRecord.accessKeyID ?? tokenRecord.ak),
        accessKeySecret: pickString(tokenRecord.accessKeySecret ?? tokenRecord.accessKeySECRET ?? tokenRecord.sk),
        securityToken: pickString(tokenRecord.securityToken ?? tokenRecord.stsToken ?? tokenRecord.token) || undefined,
        expiration: pickString(tokenRecord.expiration ?? tokenRecord.expire ?? tokenRecord.expiresAt) || undefined,
    };

    if (!credentials.accessKeyId || !credentials.accessKeySecret) {
        throw new Error('OSS 上传凭证缺失');
    }

    return credentials;
}

function extractPublicBaseUrl(value: unknown): string {
    const raw = unwrapData<unknown>(value);
    const record = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
    const candidates = [
        record.publicUrl,
        record.publicBaseUrl,
        record.host,
        record.endpoint,
        record.domain,
        typeof record.data === 'object' && record.data !== null ? (record.data as Record<string, unknown>).publicUrl : undefined,
        typeof record.data === 'object' && record.data !== null ? (record.data as Record<string, unknown>).publicBaseUrl : undefined,
        typeof record.data === 'object' && record.data !== null ? (record.data as Record<string, unknown>).host : undefined,
        typeof record.data === 'object' && record.data !== null ? (record.data as Record<string, unknown>).endpoint : undefined,
        typeof record.data === 'object' && record.data !== null ? (record.data as Record<string, unknown>).domain : undefined,
    ];

    const matched = candidates.map((item) => pickString(item)).find(Boolean);
    if (!matched) {
        return '';
    }
    return matched.replace(/\/$/, '');
}

function buildPublicImageUrl(baseUrl: string, objectKey: string): string {
    if (!baseUrl) {
        throw new Error('缺少 OSS 公共访问域名');
    }
    const normalizedBaseUrl = /^https?:\/\//.test(baseUrl) ? baseUrl : `https://${baseUrl}`;
    return `${normalizedBaseUrl}/${objectKey}`;
}


function unwrapData<T>(value: unknown): T {
    if (typeof value === 'object' && value !== null && 'data' in value) {
        return (value as { data: T }).data;
    }
    return value as T;
}

function normalizeTicketReply(value: unknown): TicketReply {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    const botName = pickString(record.bot);
    const hasBot = Boolean(botName);
    return {
        id: pickString(record.id ?? record._id ?? record.replyId),
        ticketId: pickString(record.ticketId ?? record.ticket_id),
        content: pickString(record.content ?? record.message ?? record.text),
        images: pickStringArray(record.images ?? record.imageUrls ?? record.pictures),
        // 沟通回复服务端常用 time；兼容 createdAt / timestamp
        createdAt: pickString(
            record.createdAt
                ?? record.created_at
                ?? record.time
                ?? record.timestamp
                ?? record.sendTime
                ?? record.send_time
        ),
        updatedAt: pickString(record.updatedAt ?? record.updated_at),
        senderId: pickString(record.senderId ?? record.userId ?? record.user_id ?? record.authorId ?? record.user),
        // bot 非空时视为 AI 回复，发送人名称优先取 bot
        senderName: pickString(
            botName
                || record.senderName
                || record.username
                || record.authorName
                || record.nickname
        ),
        senderAvatar: pickString(record.senderAvatar ?? record.avatar ?? record.authorAvatar),
        senderType: (
            (hasBot ? "ai" : undefined)
                || (pickString(record.senderType ?? record.role ?? record.authorType) || undefined)
        ) as TicketReply['senderType'],
        isSelf: pickBoolean(record.isSelf),
        isAi: hasBot || pickBoolean(record.isAi ?? record.ai),
    };
}

function normalizeTicketListItem(value: unknown): TicketListItem {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    // 服务端列表字段为 last_reply
    const latestReplyRaw =
        record.last_reply
            ?? record.latestReply
            ?? record.lastReply
            ?? record.latest_reply;
    const latestReply = latestReplyRaw ? normalizeTicketReply(latestReplyRaw) : null;
    return {
        id: pickString(record.id ?? record._id ?? record.ticketId),
        type: pickString(record.type ?? record.ticketType),
        title: pickString(record.title ?? record.subject),
        latestReply,
        aiTagged:
            pickBoolean(record.aiTagged ?? record.hasAiReply ?? record.ai_tagged)
            || Boolean(latestReply?.isAi),
        createdAt: pickString(record.createdAt ?? record.created_at),
        updatedAt: pickString(
            record.updatedAt
                ?? record.updated_at
                ?? latestReply?.createdAt
        ),
    };
}

function normalizeTicketDetail(value: unknown): TicketDetail {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    const rawReplies = record.replies ?? record.messages;
    const replies: TicketReply[] = Array.isArray(rawReplies)
        ? rawReplies.map((item: unknown) => normalizeTicketReply(item))
        : [];
    return {
        id: pickString(record.id ?? record._id ?? record.ticketId),
        type: pickString(record.type ?? record.ticketType),
        title: pickString(record.title ?? record.subject),
        createdAt: pickString(record.createdAt ?? record.created_at),
        updatedAt: pickString(record.updatedAt ?? record.updated_at),
        members: pickStringArray(record.members ?? record.memberIds ?? record.userIds),
        replies,
    };
}

function pickNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

/**
 * 解析时间字符串为时间戳；无效时返回 0。
 */
function parseTimeMs(value: string | undefined | null): number {
    if (!value) {
        return 0;
    }
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * 按时间降序比较（新 → 旧）。
 */
function compareTimeDesc(left: string, right: string): number {
    return parseTimeMs(right) - parseTimeMs(left);
}

/**
 * 归一化匹配订单类汇总。
 */
function normalizeMatchOrderStats(value: unknown): AgentMatchOrderStats {
    const record = (typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : {}) as Record<string, unknown>;
    return {
        drivers: pickNumber(record.drivers) ?? 0,
        pairs: pickNumber(record.pairs) ?? 0,
        orders: pickNumber(record.orders) ?? 0,
    };
}

/**
 * 归一化日报关联订单行。
 */
function normalizeRelatedOrder(value: unknown): AgentRelatedOrder {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    return {
        dayKey: pickString(record.dayKey ?? record.day_key),
        cargoId: pickString(record.cargoId ?? record.cargo_id),
        cargoName: pickString(record.cargoName ?? record.cargo_name),
        driverId: pickString(record.driverId ?? record.driver_id),
        driverName: pickString(record.driverName ?? record.driver_name),
        pushChannels: pickString(record.pushChannels ?? record.push_channels),
        pushCount: pickNumber(record.pushCount ?? record.push_count) ?? 0,
        firstPushedAt: pickString(record.firstPushedAt ?? record.first_pushed_at),
        lastPushedAt: pickString(record.lastPushedAt ?? record.last_pushed_at),
        orderId: pickString(record.orderId ?? record.order_id),
        orderStatus: pickString(record.orderStatus ?? record.order_status),
        orderCreatedAt: pickString(record.orderCreatedAt ?? record.order_created_at),
        orderCanceled: pickBoolean(record.orderCanceled ?? record.order_canceled),
        orderCreatedAfterFirstPush: pickBoolean(
            record.orderCreatedAfterFirstPush ?? record.order_created_after_first_push
        ),
        orderCreatedAfterFirstPushNonCanceled: pickBoolean(
            record.orderCreatedAfterFirstPushNonCanceled
                ?? record.order_created_after_first_push_non_canceled
        ),
        shipperId: pickString(record.shipperId ?? record.shipper_id),
        shipperName: pickString(record.shipperName ?? record.shipper_name),
        truckId: pickString(record.truckId ?? record.truck_id),
        truckPlate: pickString(record.truckPlate ?? record.truck_plate),
        requestIds: pickString(record.requestIds ?? record.request_ids),
        reasons: pickString(record.reasons),
        raw: record,
    };
}

function normalizeAgentDispatchRecord(value: unknown): AgentDispatchRecord {
    const record = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    const nested =
        typeof record.data === 'object' && record.data !== null && !Array.isArray(record.data)
            ? (record.data as Record<string, unknown>)
            : record;
    return {
        id: pickString(nested.id ?? nested._id ?? nested.recordId ?? nested.record_id),
        status: pickString(nested.status ?? nested.state ?? nested.resultStatus),
        type: pickString(nested.type ?? nested.actionType ?? nested.action_type ?? nested.operationType),
        title: pickString(nested.title ?? nested.name ?? nested.subject ?? nested.action),
        summary: pickString(nested.summary ?? nested.description ?? nested.desc ?? nested.remark),
        content: pickString(nested.content ?? nested.message ?? nested.result ?? nested.output),
        dayKey: pickString(nested.dayKey ?? nested.day_key ?? nested.date),
        driverId: pickString(nested.driverId ?? nested.driver_id ?? nested.driver),
        shipperId: pickString(
            nested.shipperId
                ?? nested.shipper_id
                ?? nested.shipper
                ?? nested.ownerId
                ?? nested.owner_id
                ?? nested.owner
        ),
        cargoId: pickString(nested.cargoId ?? nested.cargo_id ?? nested.cargo),
        waybillId: pickString(nested.waybillId ?? nested.waybill_id ?? nested.waybill),
        createdAt: pickString(nested.createdAt ?? nested.created_at ?? nested.timestamp ?? nested.time),
        updatedAt: pickString(nested.updatedAt ?? nested.updated_at),
        raw: nested,
    };
}

function extractAgentRecordList(payload: unknown): unknown[] {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (typeof payload !== 'object' || payload === null) {
        return [];
    }
    const record = payload as Record<string, unknown>;
    const candidates = [
        record.records,
        record.items,
        record.list,
        record.rows,
        record.data,
        record.agentDispatchRecords,
    ];
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }
    return [];
}

/**
 * 归一化智能体按日汇总报告。
 */
function normalizeAgentDailyReport(dayKey: string, value: unknown): AgentDailyReport {
    const payload = unwrapData<unknown>(value);
    const record = (typeof payload === 'object' && payload !== null && !Array.isArray(payload)
        ? payload
        : {}) as Record<string, unknown>;
    const statisticsRaw =
        typeof record.statistics === 'object' && record.statistics !== null && !Array.isArray(record.statistics)
            ? (record.statistics as Record<string, unknown>)
            : {};
    const scopeRaw =
        typeof record.scope === 'object' && record.scope !== null && !Array.isArray(record.scope)
            ? (record.scope as Record<string, unknown>)
            : null;
    const relatedOrdersRaw = Array.isArray(record.related_orders)
        ? record.related_orders
        : Array.isArray(record.relatedOrders)
            ? record.relatedOrders
            : [];

    return {
        dayKey: pickString(record.dayKey ?? record.day_key ?? record.date) || dayKey,
        scope: scopeRaw
            ? {
                start: pickString(scopeRaw.start),
                end: pickString(scopeRaw.end),
            }
            : null,
        statistics: {
            dispatchTotalRecords:
                pickNumber(statisticsRaw.dispatch_total_records ?? statisticsRaw.dispatchTotalRecords) ?? 0,
            dispatchUniquePairs:
                pickNumber(statisticsRaw.dispatch_unique_pairs ?? statisticsRaw.dispatchUniquePairs) ?? 0,
            dispatchUniqueDrivers:
                pickNumber(statisticsRaw.dispatch_unique_drivers ?? statisticsRaw.dispatchUniqueDrivers) ?? 0,
            matchedAnyOrder: normalizeMatchOrderStats(
                statisticsRaw.matched_any_order ?? statisticsRaw.matchedAnyOrder
            ),
            matchedNonCanceledOrder: normalizeMatchOrderStats(
                statisticsRaw.matched_non_canceled_order ?? statisticsRaw.matchedNonCanceledOrder
            ),
            matchedCreatedAfterFirstPush: normalizeMatchOrderStats(
                statisticsRaw.matched_created_after_first_push
                    ?? statisticsRaw.matchedCreatedAfterFirstPush
            ),
            matchedCreatedAfterFirstPushNonCanceled: normalizeMatchOrderStats(
                statisticsRaw.matched_created_after_first_push_non_canceled
                    ?? statisticsRaw.matchedCreatedAfterFirstPushNonCanceled
            ),
        },
        relatedOrders: relatedOrdersRaw
            .map((item) => normalizeRelatedOrder(item))
            .sort((left, right) => {
                const leftTime = left.orderCreatedAt || left.firstPushedAt || left.lastPushedAt;
                const rightTime = right.orderCreatedAt || right.firstPushedAt || right.lastPushedAt;
                return compareTimeDesc(leftTime, rightTime);
            }),
        raw: payload,
    };
}

interface gybGetListParams extends GetListParams {
    target?: string;
    targetId?: string;
}

export const dataProvider = (keycloak: Keycloak): AppDataProvider => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8055';
    const httpClient = createHttpClient(keycloak);
    const baseDataProvider = jsonServerProvider(apiUrl, httpClient);

    const myDataProvider: AppDataProvider = {
        ...baseDataProvider,
        getManyReference: async (resource: string, params: GetManyReferenceParams) => {
            const splitResource = resource.split('/');
            const parentPath = splitResource.length > 1 ? splitResource[0] : null;
            const childResource = splitResource.length > 1 ? splitResource[1] : resource;
            const { target, id } = params;
            if (resource && target) {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const query = {
                    ...fetchUtils.flattenObject(params.filter),
                    _sort: field,
                    _order: order,
                    _start: (page - 1) * perPage,
                    _end: page * perPage,
                };
                const url = parentPath
                    ? `${apiUrl}/${parentPath}/${id}/${childResource}?${stringify(query)}`
                    : `${apiUrl}/${childResource}?${stringify(query)}`;
                const { headers, json } = await httpClient(url);
                const totalCount = headers ? parseInt(headers.get('x-total-count')?.split('/').pop() || '0', 10) : 0;
                return {
                    data: json,
                    total: totalCount,
                };
            }
            return baseDataProvider.getManyReference(resource, params);
        },
        getOneWithSubResource: async (resource: string, id: string, subResource: string, subId: string) => {
            const url = `${apiUrl}/${resource}/${id}/${subResource}/${subId}`;
            const { json } = await httpClient(url);
            return { data: json };
        },
        getMany: async (resource: string, params: GetManyParams) => {
            const url = `${apiUrl}/${resource}/many?${stringify({ ids: params.ids })}`;
            const { json } = await httpClient(url);
            return { data: json };
        },
        getList: async (resource: string, params: gybGetListParams) => {
            const { target, targetId, ...otherFilters } = params.filter ?? {};
            const paths = [resource, target, targetId].filter(Boolean) as string[];
            const isAgentDispatchRecords = resource === 'agentDispatchRecords';
            /** 兼容本地缓存里旧的 snake_case 排序字段 */
            const rawSortField = params.sort?.field
                || (isAgentDispatchRecords ? 'createdAt' : 'id');
            const sortField = isAgentDispatchRecords
                ? ({
                    created_at: 'createdAt',
                    updated_at: 'updatedAt',
                }[rawSortField] ?? rawSortField)
                : rawSortField;
            const sortOrder = params.sort?.order || 'DESC';
            const query = {
                ...fetchUtils.flattenObject(otherFilters),
                _sort: sortField,
                _order: sortOrder,
                _start: (params.pagination?.page ? (params.pagination.page - 1) * params.pagination.perPage : 0),
                _end: (params.pagination?.page ? params.pagination.page * params.pagination.perPage : 10),
            };
            const url = buildUrl(apiUrl, paths, query);
            const { headers, json } = await httpClient(url);
            const totalCount = headers ? parseInt(headers.get('x-total-count')?.split('/').pop() || '0', 10) : 0;

            if (isAgentDispatchRecords) {
                const list = extractAgentRecordList(unwrapData(json)).map((item) =>
                    normalizeAgentDispatchRecord(item)
                );
                const isTimeSort =
                    sortField === 'created_at'
                    || sortField === 'createdAt'
                    || sortField === 'updated_at'
                    || sortField === 'updatedAt';
                if (isTimeSort) {
                    list.sort((left, right) => {
                        if (sortOrder === 'ASC') {
                            return compareTimeDesc(right.createdAt, left.createdAt);
                        }
                        return compareTimeDesc(left.createdAt, right.createdAt);
                    });
                }
                return {
                    data: list,
                    total: totalCount === 0 ? list.length : totalCount,
                };
            }

            return {
                data: json,
                total: totalCount === 0 ? (Array.isArray(json) ? json.length : 0) : totalCount,
            };
        },
        getTickets: async () => {
            const url = buildUrl(apiUrl, ['tickets'], { _sort: 'updatedAt', _order: 'DESC' });
            const { json } = await httpClient(url);
            const payload = unwrapData<unknown>(json);
            const records = Array.isArray(payload)
                ? payload
                : (typeof payload === 'object' && payload !== null && Array.isArray((payload as { tickets?: unknown[] }).tickets)
                    ? (payload as { tickets: unknown[] }).tickets
                    : []);
            return { data: records.map((item: unknown) => normalizeTicketListItem(item)) };
        },
        getTicketDetail: async (ticketId: string) => {
            const { json } = await httpClient(`${apiUrl}/tickets/${ticketId}`);
            const payload = unwrapData<unknown>(json);
            const record = typeof payload === 'object' && payload !== null && 'ticket' in payload
                ? (payload as { ticket?: unknown }).ticket
                : payload;
            return { data: normalizeTicketDetail(record) };
        },
        getAgentDispatchRecord: async (recordId: string) => {
            const { json } = await httpClient(`${apiUrl}/agentDispatchRecords/${recordId}`);
            const payload = unwrapData<unknown>(json);
            const record =
                typeof payload === 'object' &&
                payload !== null &&
                'record' in payload
                    ? (payload as { record?: unknown }).record
                    : payload;
            return { data: normalizeAgentDispatchRecord(record) };
        },
        getAgentDailyReport: async (dayKey: string) => {
            const url = buildUrl(apiUrl, ['agentDispatchRecords', 'report', 'daily'], {
                day_key: dayKey,
            });
            const { json } = await httpClient(url);
            return { data: normalizeAgentDailyReport(dayKey, json) };
        },
        createTicketReply: async (ticketId: string, params: CreateTicketReplyInput) => {
            const { json } = await httpClient(`${apiUrl}/tickets/${ticketId}/replies`, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: normalizeTicketReply(unwrapData<unknown>(json)) };
        },
        getOssPublicToken: async () => {
            const { json } = await httpClient(`${apiUrl}/oss/token?target=public`);
            return { data: normalizeOssTokenCredentials(json) };
        },
        uploadTicketPublicImage: async (file: File) => {
            const { json } = await httpClient(`${apiUrl}/oss/token?target=public`);
            const credentials = normalizeOssTokenCredentials(json);
            const publicBaseUrl = extractPublicBaseUrl(json);
            const objectKey = buildTicketImageObjectKey(file);
            const policy = {
                expiration: credentials.expiration ?? new Date(Date.now() + 5 * 60 * 1000).toISOString(),
                conditions: [
                    ['content-length-range', 0, Math.max(file.size, 1)],
                    { key: objectKey },
                ],
            };
            const encodedPolicy = encodeOssPolicy(policy);
            const signature = await createHmacSha1Signature(credentials.accessKeySecret, encodedPolicy);
            const formData = new FormData();
            formData.append('key', objectKey);
            formData.append('OSSAccessKeyId', credentials.accessKeyId);
            formData.append('policy', encodedPolicy);
            formData.append('Signature', signature);
            if (credentials.securityToken) {
                formData.append('x-oss-security-token', credentials.securityToken);
            }
            formData.append('success_action_status', '200');
            formData.append('file', file);

            const uploadTarget = buildPublicImageUrl(publicBaseUrl, '');
            const response = await fetch(uploadTarget.endsWith('/') ? uploadTarget : `${uploadTarget}/`, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                throw new Error('图片上传失败');
            }
            return {
                data: {
                    url: buildPublicImageUrl(publicBaseUrl, objectKey),
                },
            };
        },
        deleteTicketReply: async ({ ticketId, replyId }) => {
            const { json } = await httpClient(`${apiUrl}/tickets/${ticketId}/replies/${replyId}`, {
                method: 'DELETE',
            });
            const normalized = unwrapData<unknown>(json);
            if (normalized === null || normalized === undefined || normalized === '') {
                return { data: { id: replyId } };
            }
            if (typeof normalized === 'object' && normalized !== null && ('id' in normalized || '_id' in normalized || 'replyId' in normalized)) {
                return { data: normalizeTicketReply(normalized) };
            }
            return { data: { id: replyId } };
        },
        sendPushNotification: async (params: unknown) => {
            const url = `${apiUrl}/push/mobile-system-push`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        sendSmsNotification: async (params: unknown) => {
            const url = `${apiUrl}/push/sms-push`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        fetchPushRecord: async (userId: string) => {
            const url = `${apiUrl}/push/push-record/${userId}`;
            const { json } = await httpClient(url);
            return { data: json };
        },
        fetchUserInfo: async (userId: string) => {
            const url = `${apiUrl}/users/many?ids=${userId}`;
            const { json } = await httpClient(url);
            return { data: json };
        },
        fetchUserLoginToken: async (userId: string) => {
            const url = `${apiUrl}/users/login`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify({ id: userId }),
            });
            const token = extractUsersLoginToken(json);
            if (!token) {
                throw new Error('无法从 /users/login 响应中解析 token');
            }
            return { data: token };
        },
        fetchUserCargoStats: async (userId: string, days: number) => {
            const url = `${apiUrl}/users/cargo-stats/${userId}?days=${days}`;
            const { json } = await httpClient(url);
            return json;
        },
        sendManualReview: async (params: ManualReviewParams) => {
            const url = `${apiUrl}/verify/manual`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        getVerifyImages: async (verifyId: string) => {
            const url = `${apiUrl}/verify/user/${verifyId}/images`;
            const { json } = await httpClient(url);
            return json;
        },
        getTruckVerifyImages: async (verifyId: string) => {
            const url = `${apiUrl}/verify/truck/${verifyId}/images`;
            const { json } = await httpClient(url);
            return json;
        },
        getVerifyImageData: async (imageId: string) => {
            const url = `${apiUrl}/verify/static/image/${imageId}`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${keycloak.token}`,
                },
            });
            const arrayBuffer = await response.arrayBuffer();
            const base64 = btoa(
                new Uint8Array(arrayBuffer)
                    .reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            return `data:image/jpeg;base64,${base64}`;
        },
        modifyUserStatus: async (verifyId: string, params: UpdateVerifyStatusParams) => {
            const url = `${apiUrl}/verify/user/${verifyId}`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        modifyTruckStatus: async (verifyId: string, params: UpdateVerifyStatusParams) => {
            const url = `${apiUrl}/verify/truck/${verifyId}`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        addCargoPrice: async (params: RefPriceOrder) => {
            const url = `${apiUrl}/cargos/price`;
            const { json } = await httpClient(url, {
                method: 'PUT',
                body: JSON.stringify(params),
            });
            return { data: json };
        },
        driversByLocation: async (params: QueryLocationDriversParams) => {
            const url = `${apiUrl}/drivers/by_pos?lat=${params.lat}&lon=${params.lon}&distance=${params.distance}&pos_from=${params.pos_from}&pos_to=${params.pos_to}`;
            const { json } = await httpClient(url, {
                method: 'GET',
            });
            return { data: json };
        },
        querySettlementInfo: async (waybillId: string) => {
            const url = `${apiUrl}/waybills/${waybillId}/checkout`;
            const { json } = await httpClient(url, {
                method: 'GET',
            });
            return { data: json };
        },
        commitSettlementData: async (
            waybillId: string,
            params: { amount: number; weight: number; unit_price: number; single_price?: number | string | null }
        ) => {
            const url = buildUrl(apiUrl, ['waybills', waybillId, 'checkout'], {
                amount: params.amount,
                weight: params.weight,
                unit_price: params.unit_price,
                single_price: params.single_price ?? '',
            });
            const { json } = await httpClient(url, {
                method: 'POST',
            });
            return { data: json };
        },
        getAlipayBalance: async () => {
            const url = `${apiUrl}/billings/alipay-balance`;
            const { json } = await httpClient(url, {
                method: 'GET',
            });
            return { data: json };
        },
        fetchWaybillTracking: async (params: { order: string; alt?: number; token: string }) => {
            const trackingApiUrl = 'https://trkg.guiyunbao.cn';
            const normalizedTrackingApiUrl = trackingApiUrl.endsWith('/')
                ? trackingApiUrl.slice(0, -1)
                : trackingApiUrl;
            const url = buildUrl(normalizedTrackingApiUrl, ['location', 'tracking'], {
                order: params.order,
                alt: params.alt ?? 1,
            });
            const { json } = await fetchUtils.fetchJson(url, {
                method: 'GET',
                headers: new Headers({
                    Accept: 'application/json',
                    Authorization: `Bearer ${params.token}`,
                    'X-App-Version': '2.3.20',
                }),
            });
            return { data: json };
        },
        resetThirdPartyClientSecret: async (id: string) => {
            const url = `${apiUrl}/thirdPartyClients/${id}/reset-secret`;
            const { json } = await httpClient(url, {
                method: 'POST',
            });
            return { data: json };
        },
    };

    return myDataProvider;
};
